import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRoadTrip } from '../hooks/useRoadTrip';
import { format, parseISO } from 'date-fns';
import RoadTripMap from '../components/RoadTripMap';
import RoadTripWaypointCard from '../components/RoadTripWaypointCard';
import RoadTripCostBreakdown from '../components/RoadTripCostBreakdown';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import type { Location, TripDay, TripStop, RoadTrip, RoadTripWaypoint as RoadTripWaypointType } from '../types/roadTrip';

interface RoadTripPlannerProps {
  user: { id: string; email: string } | null;
}

export default function RoadTripPlanner({ user }: RoadTripPlannerProps) {
  const { tripId } = useParams();
  const navigate = useNavigate();

  const {
    trip, setTrip, preferences, updatePreferences,
    addWaypoint, removeWaypoint, reorderWaypoints, updateWaypoint,
    optimizeRoute, calculateTotals, generateDayByDay,
    getCostBreakdown
  } = useRoadTrip();

  const [showAddWaypoint, setShowAddWaypoint] = useState(false);
  const [newWaypoint, setNewWaypoint] = useState({
    name: '',
    location: { name: '', address: '', lat: 0, lng: 0 } as Location,
    type: 'stop' as const,
    duration: 2,
    isRequired: true
  });
  const [optimizedRoute, setOptimizedRoute] = useState<ReturnType<typeof optimizeRoute>>(null);
  const [tripDays, setTripDays] = useState<ReturnType<typeof generateDayByDay>>([]);

  // Initialize trip if we have a tripId
  useEffect(() => {
    if (tripId) {
      // In real app, fetch trip from Supabase
      // For now, create a demo trip
      if (!trip) {
        setTrip({
          id: tripId,
          userId: user?.id || 'demo',
          title: 'Road Trip Adventure',
          startLocation: { name: 'Start', lat: 0, lng: 0 },
          endLocation: { name: 'End', lat: 0, lng: 0 },
          startDate: format(new Date(), 'yyyy-MM-dd'),
          endDate: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
          waypoints: [],
          totalDistance: 0,
          totalDuration: 0,
          vehicleType: 'car',
          fuelType: 'gasoline',
          fuelEfficiency: 10,
          estimatedFuelCost: 0,
          estimatedTollCost: 0,
          totalCost: 0,
          currency: 'USD',
          status: 'planning',
          segments: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }
    }
  }, [tripId, user]);

  // Update calculations when waypoints change
  useEffect(() => {
    if (trip) {
      calculateTotals();
      const optimized = optimizeRoute();
      setOptimizedRoute(optimized);
      setTripDays(generateDayByDay());
    }
  }, [trip?.waypoints]);

  const handleAddWaypoint = () => {
    if (!newWaypoint.name || !newWaypoint.location.name) return;

    addWaypoint({
      name: newWaypoint.name,
      location: newWaypoint.location,
      type: newWaypoint.type,
      duration: newWaypoint.duration,
      isRequired: newWaypoint.isRequired
    });

    setNewWaypoint({
      name: '',
      location: { name: '', address: '', lat: 0, lng: 0 },
      type: 'stop',
      duration: 2,
      isRequired: true
    });
    setShowAddWaypoint(false);
  };

  const handleOptimizeRoute = () => {
    const optimized = optimizeRoute();
    if (optimized && trip) {
      setTrip((prev: RoadTrip | null) => prev ? {
        ...prev,
        waypoints: optimized.waypoints
      } : null);
    }
  };

  if (!trip) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                ← Back
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">{trip.title}</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleOptimizeRoute}>
                Optimize Route
              </Button>
              <Button variant="primary" onClick={() => setShowAddWaypoint(true)}>
                + Add Stop
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Waypoints */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trip Overview */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Trip Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Start Date</p>
                  <p className="font-medium">{format(parseISO(trip.startDate), 'MMM d, yyyy')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">End Date</p>
                  <p className="font-medium">{format(parseISO(trip.endDate), 'MMM d, yyyy')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Distance</p>
                  <p className="font-medium">{trip.totalDistance} km</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Est. Duration</p>
                  <p className="font-medium">{Math.round(trip.totalDuration)} hours</p>
                </div>
              </div>
            </div>

            {/* Waypoints */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">
                Stops ({trip.waypoints.length})
              </h2>

              {trip.waypoints.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">No stops added yet</p>
                  <Button variant="primary" onClick={() => setShowAddWaypoint(true)}>
                    Add Your First Stop
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {trip.waypoints.map((waypoint: RoadTripWaypointType, index: number) => (
                    <RoadTripWaypointCard
                      key={waypoint.id}
                      waypoint={waypoint}
                      index={index}
                      totalWaypoints={trip.waypoints.length}
                      onUpdate={(updates: Partial<RoadTripWaypointType>) => updateWaypoint(waypoint.id, updates)}
                      onRemove={() => removeWaypoint(waypoint.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Day by Day */}
            {tripDays.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4">Day by Day</h2>
                <div className="space-y-4">
                  {tripDays.map((day: TripDay, index: number) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium">Day {index + 1}</h3>
                        <span className="text-sm text-gray-500">{day.date}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Distance</p>
                          <p className="font-medium">{day.distance} km</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Driving</p>
                          <p className="font-medium">{day.drivingTime} hours</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Stops</p>
                          <p className="font-medium">{day.totalStops}</p>
                        </div>
                      </div>
                      {day.stops.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm font-medium mb-2">Stops:</p>
                          <div className="space-y-1">
                            {day.stops.map((stop: TripStop, i: number) => (
                              <p key={i} className="text-sm text-gray-600">
                                • {stop.waypoint.name}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Map & Cost */}
          <div className="space-y-6">
            {/* Map */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <RoadTripMap
                waypoints={trip.waypoints}
                segments={optimizedRoute?.segments || []}
              />
            </div>

            {/* Cost Breakdown */}
            <RoadTripCostBreakdown
              breakdown={getCostBreakdown()}
              tripDays={tripDays.length}
            />

            {/* Preferences */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Preferences</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600">Max Driving/Day</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="4"
                      max="10"
                      value={preferences.maxDrivingHoursPerDay}
                      onChange={(e) => updatePreferences({ maxDrivingHoursPerDay: Number(e.target.value) })}
                      className="flex-1"
                    />
                    <span className="font-medium">{preferences.maxDrivingHoursPerDay}h</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Max Distance/Day</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="200"
                      max="800"
                      step="50"
                      value={preferences.maxDistancePerDay}
                      onChange={(e) => updatePreferences({ maxDistancePerDay: Number(e.target.value) })}
                      className="flex-1"
                    />
                    <span className="font-medium">{preferences.maxDistancePerDay}km</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Avoid Tolls</span>
                  <button
                    onClick={() => updatePreferences({ avoidTolls: !preferences.avoidTolls })}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      preferences.avoidTolls ? 'bg-primary-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                      preferences.avoidTolls ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Scenic Routes</span>
                  <button
                    onClick={() => updatePreferences({
                      preferredRoadTypes: preferences.preferredRoadTypes.includes('scenic')
                        ? preferences.preferredRoadTypes.filter((t: string) => t !== 'scenic')
                        : [...preferences.preferredRoadTypes, 'scenic']
                    })}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      preferences.preferredRoadTypes.includes('scenic') ? 'bg-primary-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                      preferences.preferredRoadTypes.includes('scenic') ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Waypoint Modal */}
      <Modal
        isOpen={showAddWaypoint}
        onClose={() => setShowAddWaypoint(false)}
        title="Add Stop"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stop Name
            </label>
            <input
              type="text"
              value={newWaypoint.name}
              onChange={(e) => setNewWaypoint(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Grand Canyon"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <input
              type="text"
              value={newWaypoint.location.name}
              onChange={(e) => setNewWaypoint(prev => ({
                ...prev,
                location: { ...prev.location, name: e.target.value }
              }))}
              placeholder="e.g., Phoenix, AZ"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stop Type
            </label>
            <select
              value={newWaypoint.type}
              onChange={(e) => setNewWaypoint(prev => ({
                ...prev,
                type: e.target.value as any
              }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="start">Starting Point</option>
              <option value="end">Destination</option>
              <option value="stop">Regular Stop</option>
              <option value="overnight">Overnight Stay</option>
              <option value="attraction">Attraction</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duration (hours)
            </label>
            <input
              type="number"
              min="0.5"
              max="24"
              step="0.5"
              value={newWaypoint.duration}
              onChange={(e) => setNewWaypoint(prev => ({
                ...prev,
                duration: Number(e.target.value)
              }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="required"
              checked={newWaypoint.isRequired}
              onChange={(e) => setNewWaypoint(prev => ({
                ...prev,
                isRequired: e.target.checked
              }))}
              className="h-4 w-4 text-primary-500 rounded"
            />
            <label htmlFor="required" className="ml-2 text-sm text-gray-600">
              Required stop (cannot be skipped by route optimizer)
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowAddWaypoint(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddWaypoint}
              disabled={!newWaypoint.name || !newWaypoint.location.name}
            >
              Add Stop
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
